import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { useFormikContext } from 'formik';
import _ from 'lodash';

import { useT } from '@features/oneOnboarding/hooks/useT';
import { OOControlModelInterface } from '@features/oneOnboarding/interfaces';
import { OOPageModel } from '@features/oneOnboarding/models/FormModel';
import { OOStepModel } from '@features/oneOnboarding/models/StepModel';
import { OOFlowWrapper } from '@features/oneOnboarding/wrappers/FlowWrapper';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import { FormControl } from '@mui/material';
import Button from '@mui/material/Button';
import { store } from '@store/index';
import { oneOnboardingSlice } from '@store/slices/oneOnboarding.slice';

import { FormControlLoader } from '../../FormControlLoader';

import styles from './EditAndCancelButton.module.scss';

type OOEditAndCancelButtonProps = {
  controlModel: OOControlModelInterface;
  otherFormControls: OOControlModelInterface[];
};

enum EDITANDCANCEL_ENUM {
  EDIT = 'edit',
  CANCEL = 'cancel',
}

export const OOEditAndCancelButton: React.FC<OOEditAndCancelButtonProps> = ({
  controlModel,
  otherFormControls,
}: OOEditAndCancelButtonProps) => {
  const { t, translationsLoading } = useT('entry', 'approval', 'documents', 'hiring');
  const [showEditButton, setShowEditButton] = useState<boolean>(true);
  const { flow: flowData, stepData: currentStep, form: currentPage, currentForm } = store.getState().oneOnboarding;

  const dispatch = useDispatch();
  const formik = useFormikContext<Record<string, any>>();

  const currentStepIndex: number = useMemo(
    () => flowData.findIndex((flow: OOStepModel) => flow?.name === currentStep?.name),
    [flowData, currentStep],
  );
  const currentPageIndex: number = useMemo(
    () => flowData?.[currentStepIndex].pages.findIndex((page: OOPageModel) => page.name === currentPage),
    [flowData, currentPage, currentStepIndex],
  );
  const tempFlowData: OOStepModel[] = useMemo(() => _.cloneDeep(flowData), [flowData]);
  const tempCurrentPage = tempFlowData?.[currentStepIndex]?.pages?.[currentPageIndex];

  const formControlValueMapper: any = {};
  otherFormControls?.forEach(
    (control: OOControlModelInterface) => (formControlValueMapper[control.name] = control.value),
  );

  const initialControlValues: any[] = [];

  controlModel?.editableControls?.forEach(
    (control) =>
      formControlValueMapper?.[control?.name] && initialControlValues.push(formControlValueMapper?.[control?.name]),
  );

  const discardEditControl =
    !controlModel?.isApplicableInitialFlow && !initialControlValues?.length && currentForm === currentPage;

  const updateEnableEditingStatus = useCallback(
    (enableEditing: boolean, hideSubmitButton: boolean) => {
      tempCurrentPage.hideSubmitButton = hideSubmitButton;
      controlModel.editableControls?.forEach((editableControl: any) => {
        if (enableEditing) {
          editableControl?.clearControlOnEdit && formik.setFieldValue(editableControl?.control?.name, '');
        } else {
          const control: OOControlModelInterface | undefined = otherFormControls.find(
            (control: OOControlModelInterface) => control?.name === editableControl?.control?.name,
          );
          control &&
            formik.setFieldValue(
              editableControl.control.name,
              flowWrapper?.getUserValueOrDefault(control?.name, control?.type),
            );
        }
        tempCurrentPage.controls.forEach((control: OOControlModelInterface) => {
          if (control.name === editableControl.control.name) {
            control.isEditableCandidate = enableEditing ? editableControl?.control?.isEditableCandidate : enableEditing;
            control.isEditableRecruiter = enableEditing ? editableControl?.control?.isEditableRecruiter : enableEditing;
          }
        });
      });
      dispatch(oneOnboardingSlice.actions.setFlow({ flow: tempFlowData }));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tempFlowData],
  );

  const updateSubmitButttonVisibility = useCallback(
    (enableSubmit: boolean) => {
      tempCurrentPage.hideSubmitButton = !enableSubmit;
      dispatch(oneOnboardingSlice.actions.setFlow({ flow: tempFlowData }));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tempFlowData],
  );

  const handleEditAndCancel = (e: React.MouseEvent<HTMLButtonElement>) => {
    const enableEditing = e?.currentTarget?.id === EDITANDCANCEL_ENUM.EDIT;
    setShowEditButton(!enableEditing);
    updateEnableEditingStatus(enableEditing, enableEditing);
  };

  const handleSave = () => {
    if (_.isEmpty(formik.errors)) {
      const newValues = Object.assign({}, formik.values, { SKIP_FORWARD: true });
      newValues.PAYMENT_METHOD = !!newValues.PAYMENT_METHOD;
      formik.setValues(newValues);
      formik.handleSubmit();
      setShowEditButton(true);
      updateSubmitButttonVisibility(true);
    }
  };

  useEffect(() => {
    if (discardEditControl) {
      updateEnableEditingStatus(true, false);
    }
    // eslint-disable-next-line
  }, []);

  const flowWrapper = useMemo(() => {
    if (!flowData) return null;
    return OOFlowWrapper.create(flowData);
  }, [flowData]);

  if (translationsLoading) {
    return <FormControlLoader />;
  }

  return discardEditControl ? (
    <></>
  ) : (
    <FormControl className={styles.buttonContainer}>
      {showEditButton ? (
        <Button
          startIcon={<EditIcon style={{ color: controlModel?.textColor || undefined }} />}
          style={{ color: controlModel?.textColor || undefined }}
          id={EDITANDCANCEL_ENUM.EDIT}
          variant="text"
          fullWidth={false}
          onClick={handleEditAndCancel}
        >
          {t(controlModel.label)}
        </Button>
      ) : (
        <>
          <Button
            startIcon={<CancelIcon style={{ color: controlModel?.textColor || undefined }} />}
            style={{ color: controlModel?.textColor || undefined }}
            variant="text"
            id={EDITANDCANCEL_ENUM.CANCEL}
            fullWidth={false}
            onClick={handleEditAndCancel}
          >
            {t('candidate_recruiter:GENERAL.GENERIC.cancel')}
          </Button>
          <Button
            className={styles.buttonContainer__saveButton}
            data-testid="saveButton"
            variant="contained"
            color="primary"
            type="button"
            onClick={handleSave}
          >
            {t('candidate_recruiter:GENERAL.GENERIC.saveChanges')}
          </Button>
        </>
      )}
    </FormControl>
  );
};
